/**
 * CompanySchemaEditor — volledige schema builder voor bedrijfsaccounts.
 * Gebruikt bedrijfseigen oefenendatabase + alle globale oefeningen.
 * Vergelijkbaar met SchemaBuilder.js maar voor de B2B context.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, Trash2, Save, X, Search, GripVertical, ArrowLeft,
  ChevronDown, ChevronUp, Dumbbell, Link2, Play,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const RUST_OPTIES = ["30 sec", "45 sec", "60 sec", "90 sec", "2:00", "2:30", "3:00", "4:00", "5:00"];
const SPIERGROEPEN = ["Benen", "Billen", "Borst", "Rug", "Schouders", "Biceps", "Triceps", "Core", "Full Body", "Kuiten", "Conditie", "Anders"];
const DOELEN = ["Vetverlies", "Spieropbouw", "Onderhoud", "Powerlifting", "Conditie", "Kracht"];
const NIVEAUS = ["Beginner", "Gevorderd", "Advanced"];

function uid() { return Math.random().toString(36).slice(2, 9); }

function extractYouTubeId(url = "") {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// ── Sortable oefening rij ─────────────────────────────────────────────────────
function SortableExRow({ ex, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: ex.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="bg-[#141414] border border-white/6 rounded-xl overflow-hidden mb-2">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <button {...attributes} {...listeners} className="text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing p-0.5">
          <GripVertical size={15} />
        </button>
        <div className="flex-1 font-semibold text-white text-sm truncate">{ex.naam}</div>
        <span className="text-zinc-600 text-xs shrink-0">{ex.spiergroep}</span>
        {ex.company_only && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold shrink-0" style={{ background: `${GOLD}15`, color: GOLD }}>eigen</span>
        )}
        <button onClick={onDelete} className="text-zinc-600 hover:text-red-400 transition-colors p-1">
          <Trash2 size={13} />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
        {[
          { label: "Sets", key: "sets", type: "number", placeholder: "3", min: 1, max: 20 },
          { label: "Reps", key: "reps", type: "text", placeholder: "8-10" },
          { label: "Tempo", key: "tempo", type: "text", placeholder: "3-1-1-0" },
          { label: "Gewicht", key: "gewicht", type: "text", placeholder: "60 kg" },
        ].map(({ label, key, type, placeholder, min, max }) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">{label}</span>
            <input
              type={type} value={ex[key] ?? ""} placeholder={placeholder}
              min={min} max={max}
              onChange={e => onUpdate({ ...ex, [key]: type === "number" ? (parseInt(e.target.value) || 1) : e.target.value })}
              className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors"
            />
          </label>
        ))}
        <label className="flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Rusttijd</span>
          <select value={ex.rust ?? "90 sec"} onChange={e => onUpdate({ ...ex, rust: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
            {RUST_OPTIES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="col-span-2 sm:col-span-3 flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Notities / coaching cues</span>
          <input type="text" value={ex.notities ?? ""} placeholder="Hou je rug recht..."
            onChange={e => onUpdate({ ...ex, notities: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
      </div>
    </div>
  );
}

// ── Oefening Picker Modal ─────────────────────────────────────────────────────
function ExPickerModal({ token, onSelect, onClose, titel = "Oefening selecteren" }) {
  const [query, setQuery] = useState("");
  const [spierFilter, setSpierFilter] = useState("");
  const [catFilter, setCatFilter] = useState("alles");   // "alles" | "kracht" | "fysio"
  const [exercises, setExercises] = useState([]);
  const [newForm, setNewForm] = useState({ naam: "", spiergroep: "Anders", type: "", apparaat: "" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/company/exercises`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setExercises(d.exercises || []));
  }, [token]);

  const filtered = exercises.filter(e => {
    const matchQuery = !query || e.naam.toLowerCase().includes(query.toLowerCase());
    const matchSpier = !spierFilter || e.spiergroep === spierFilter;
    const matchCat = catFilter === "alles"
      ? true
      : catFilter === "fysio"
        ? e.categorie === "fysio"
        : catFilter === "conditie"
          ? e.spiergroep === "Conditie"
          : !e.categorie || e.categorie === "kracht" || !e.company_only;
    return matchQuery && matchSpier && matchCat;
  });

  const handleAdd = async () => {
    if (!newForm.naam.trim()) return;
    setAdding(true);
    const r = await fetch(`${API}/api/company/exercises`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(newForm),
    });
    const data = await r.json();
    const newEx = { ...newForm, company_only: true };
    setExercises(p => [...p, newEx]);
    onSelect(data.ok ? newEx : newForm);
    onClose();
    setAdding(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <h3 className="text-white font-bold text-sm">{titel}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="px-4 pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2">
            <Search size={13} className="text-zinc-500 shrink-0" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Zoek oefening..." className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600" />
          </div>
          {/* Categorie filter */}
          <div className="flex gap-1.5">
            {[
              { key: "alles",    label: "Alles" },
              { key: "kracht",   label: "Kracht" },
              { key: "conditie", label: "Conditie" },
              { key: "fysio",    label: "Fysio" },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setCatFilter(key)}
                data-testid={`cat-filter-${key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                  catFilter === key
                    ? "bg-[#C9A84C] text-black border-[#C9A84C]"
                    : "bg-[#1a1a1a] text-zinc-400 border-white/8 hover:text-white"
                }`}>{label}
              </button>
            ))}
          </div>
          {/* Spiergroep filter */}
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setSpierFilter("")} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${!spierFilter ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>Alle spieren</button>
            {SPIERGROEPEN.map(s => (
              <button key={s} onClick={() => setSpierFilter(spierFilter === s ? "" : s)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${spierFilter === s ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {filtered.map((ex, i) => (
            <button key={i} onClick={() => { onSelect(ex); onClose(); }}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-[#1a1a1a] transition-colors text-left group">
              <div>
                <span className="text-white text-sm font-medium group-hover:text-[#C9A84C] transition-colors block">{ex.naam}</span>
                {(ex.type || ex.apparaat) && <span className="text-zinc-600 text-xs">{[ex.type, ex.apparaat].filter(Boolean).join(" · ")}</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                {/* Video indicator */}
                {ex.video_url && extractYouTubeId(ex.video_url) && (
                  <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-600/10 text-red-400" title="Bevat instructievideo">
                    <Play size={8} fill="currentColor" /> Video
                  </span>
                )}
                {ex.company_only && ex.categorie === "fysio" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-500/10 text-blue-400">fysio</span>
                )}
                {ex.spiergroep === "Conditie" && !ex.company_only && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-cyan-500/10 text-cyan-400">conditie</span>
                )}
                {ex.company_only && ex.categorie !== "fysio" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${GOLD}15`, color: GOLD }}>eigen</span>
                )}
                <span className="text-zinc-600 text-xs">{ex.spiergroep}</span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-zinc-600 text-sm text-center py-6">Geen oefeningen gevonden</p>}
        </div>
        <div className="px-4 py-3 border-t border-white/8 space-y-2">
          <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wider">Eigen oefening toevoegen (alleen zichtbaar voor jouw bedrijf)</p>
          <div className="flex gap-2">
            <input value={newForm.naam} onChange={e => setNewForm(f => ({ ...f, naam: e.target.value }))}
              placeholder="Naam oefening" data-testid="new-exercise-naam"
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] placeholder-zinc-600"
              onKeyDown={e => e.key === "Enter" && handleAdd()} />
            <select value={newForm.spiergroep} onChange={e => setNewForm(f => ({ ...f, spiergroep: e.target.value }))}
              className="w-28 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none appearance-none">
              {SPIERGROEPEN.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <select value={newForm.type} onChange={e => setNewForm(f => ({ ...f, type: e.target.value }))}
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none appearance-none">
              <option value="">Type (opt.)</option>
              <option>Compound</option><option>Isolatie</option>
            </select>
            <select value={newForm.apparaat} onChange={e => setNewForm(f => ({ ...f, apparaat: e.target.value }))}
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none appearance-none">
              <option value="">Apparaat (opt.)</option>
              <option>Barbell</option><option>Dumbbell</option><option>Machine</option>
              <option>Kabel</option><option>Bodyweight</option><option>Anders</option>
            </select>
          </div>
          <button onClick={handleAdd} disabled={adding || !newForm.naam.trim()} data-testid="add-company-exercise-btn"
            className="w-full py-2 rounded-xl text-black text-sm font-bold disabled:opacity-40 transition-opacity"
            style={{ background: GOLD }}>
            {adding ? "Toevoegen..." : "Toevoegen & selecteren"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Superset picker modal (2-staps: kies oef 1, dan oef 2) ───────────────────
function SupersetPickerModal({ token, onSelect, onClose }) {
  const [step, setStep] = useState(1); // 1 of 2
  const [ex1, setEx1] = useState(null);

  const handleSelect = (ex) => {
    if (step === 1) {
      setEx1(ex);
      setStep(2);
      // ExPickerModal roept onClose aan na onSelect — geef no-op mee voor stap 1
      // zodat het venster OPEN blijft voor oefening 2
    } else {
      onSelect(ex1, ex);
      onClose();
    }
  };

  return (
    <ExPickerModal
      token={token}
      onSelect={handleSelect}
      // Stap 1: geen-op zodat modal niet sluit na selectie oefening 1
      // Stap 2: echte onClose zodat modal sluit na selectie oefening 2
      onClose={step === 1 ? () => {} : onClose}
      titel={step === 1
        ? "Superset — Kies oefening 1"
        : `Superset — Kies oefening 2  (na: ${ex1?.naam})`}
    />
  );
}

// ── Superset rij in DagEditor ─────────────────────────────────────────────────
function SupersetRow({ superset, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: superset.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const updateSub = (si, field, val) => {
    const nieuweOef = superset.oefeningen.map((o, i) =>
      i === si ? { ...o, [field]: val } : o
    );
    onUpdate({ ...superset, oefeningen: nieuweOef });
  };

  const inputCls = "bg-[#111] border border-white/8 rounded-lg px-2 py-1.5 text-white text-xs outline-none focus:border-[#C9A84C]/50 w-full";

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: `${GOLD}35` }}>
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ background: `${GOLD}10`, borderColor: `${GOLD}25` }}>
          <button {...listeners} {...attributes} className="cursor-grab text-zinc-600 hover:text-zinc-400 touch-none p-0.5">
            <GripVertical size={12} />
          </button>
          <Link2 size={12} style={{ color: GOLD }} />
          <span className="text-xs font-black uppercase tracking-widest" style={{ color: GOLD }}>Superset</span>
          <span className="text-zinc-600 text-xs ml-1">{superset.oefeningen?.map(o => o.naam).join(" + ")}</span>
          <button onClick={onDelete} className="ml-auto text-zinc-600 hover:text-red-400 transition-colors">
            <Trash2 size={12} />
          </button>
        </div>

        {/* Sub-oefeningen */}
        {(superset.oefeningen || []).map((sub, si) => (
          <div key={si} className={`px-3 py-2.5 ${si < superset.oefeningen.length - 1 ? "border-b border-white/5" : ""}`}>
            <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest mb-2">
              Oefening {si + 1}: <span className="text-white normal-case font-semibold tracking-normal">{sub.naam}</span>
            </p>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-zinc-600 text-[9px] uppercase tracking-wide block mb-1">Sets</label>
                <input type="number" value={sub.sets || 3} min={1} max={20}
                  onChange={e => updateSub(si, "sets", parseInt(e.target.value) || 1)}
                  className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-600 text-[9px] uppercase tracking-wide block mb-1">Reps</label>
                <input value={sub.reps || "10"}
                  onChange={e => updateSub(si, "reps", e.target.value)}
                  className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-600 text-[9px] uppercase tracking-wide block mb-1">Gewicht</label>
                <input value={sub.gewicht || ""} placeholder="–"
                  onChange={e => updateSub(si, "gewicht", e.target.value)}
                  className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-600 text-[9px] uppercase tracking-wide block mb-1">Tempo</label>
                <input value={sub.tempo || ""} placeholder="–"
                  onChange={e => updateSub(si, "tempo", e.target.value)}
                  className={inputCls} />
              </div>
            </div>
          </div>
        ))}

        {/* Gemeenschappelijke rust */}
        <div className="px-3 py-2 flex items-center gap-3" style={{ background: "#0a0a0a" }}>
          <span className="text-zinc-600 text-[10px] uppercase tracking-wide font-bold shrink-0">Rust</span>
          <select value={superset.rust || "90 sec"}
            onChange={e => onUpdate({ ...superset, rust: e.target.value })}
            className="bg-[#111] border border-white/8 rounded-lg px-2 py-1 text-white text-xs outline-none focus:border-[#C9A84C]/50">
            {RUST_OPTIES.map(r => <option key={r}>{r}</option>)}
          </select>
          <span className="text-zinc-700 text-[10px]">tussen supersets</span>
        </div>
      </div>
    </div>
  );
}

// ── Dag editor ────────────────────────────────────────────────────────────────
function DagEditor({ dag, onChange, token }) {
  const [showPicker, setShowPicker] = useState(false);
  const [showSupersetPicker, setShowSupersetPicker] = useState(false);
  const [open, setOpen] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = dag.oefeningen.findIndex(e => e.id === active.id);
    const newIdx = dag.oefeningen.findIndex(e => e.id === over.id);
    onChange({ ...dag, oefeningen: arrayMove(dag.oefeningen, oldIdx, newIdx) });
  };

  const addOefening = (ex) => {
    const newEx = {
      id: uid(), naam: ex.naam, spiergroep: ex.spiergroep || "",
      company_only: !!ex.company_only, sets: 3, reps: "10",
      rust: "90 sec", tempo: "", gewicht: "", notities: ex.uitvoeringstip || "",
      video_url: ex.video_url || "",
    };
    onChange({ ...dag, oefeningen: [...(dag.oefeningen || []), newEx] });
  };

  const addSuperset = (ex1, ex2) => {
    const newSS = {
      id: uid(),
      type: "superset",
      oefeningen: [
        { id: uid(), naam: ex1.naam, spiergroep: ex1.spiergroep || "", sets: 3, reps: "10", gewicht: "", tempo: "" },
        { id: uid(), naam: ex2.naam, spiergroep: ex2.spiergroep || "", sets: 3, reps: "10", gewicht: "", tempo: "" },
      ],
      rust: "60 sec",
    };
    onChange({ ...dag, oefeningen: [...(dag.oefeningen || []), newSS] });
  };

  const updateOefening = (id, updated) => {
    onChange({ ...dag, oefeningen: dag.oefeningen.map(e => e.id === id ? updated : e) });
  };

  const deleteOefening = (id) => {
    onChange({ ...dag, oefeningen: dag.oefeningen.filter(e => e.id !== id) });
  };

  const oefeningCount = (dag.oefeningen || []).reduce((s, ex) =>
    s + (ex.type === "superset" ? (ex.oefeningen?.length || 2) : 1), 0);

  return (
    <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden mb-4">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
        <input
          value={dag.naam}
          onChange={e => onChange({ ...dag, naam: e.target.value })}
          placeholder="Dag naam (bijv. Push dag)"
          className="flex-1 bg-transparent text-white font-bold text-sm outline-none placeholder:text-zinc-600 focus:placeholder:text-zinc-700"
          data-testid="dag-naam-input"
        />
        <span className="text-zinc-600 text-xs shrink-0">{oefeningCount} oef.</span>
        <button onClick={() => setOpen(o => !o)} className="text-zinc-500 hover:text-white transition-colors p-1">
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>

      {open && (
        <div className="p-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={(dag.oefeningen || []).map(e => e.id)} strategy={verticalListSortingStrategy}>
              {(dag.oefeningen || []).map(ex =>
                ex.type === "superset" ? (
                  <SupersetRow
                    key={ex.id} superset={ex}
                    onUpdate={updated => updateOefening(ex.id, updated)}
                    onDelete={() => deleteOefening(ex.id)}
                  />
                ) : (
                  <SortableExRow
                    key={ex.id} ex={ex}
                    onUpdate={updated => updateOefening(ex.id, updated)}
                    onDelete={() => deleteOefening(ex.id)}
                  />
                )
              )}
            </SortableContext>
          </DndContext>

          {(dag.oefeningen || []).length === 0 && (
            <div className="text-center py-6 border border-dashed border-white/8 rounded-xl mb-3">
              <Dumbbell size={22} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-600 text-sm">Nog geen oefeningen — voeg er een toe</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setShowPicker(true)}
              data-testid="add-oefening-btn"
              className="flex-1 py-2.5 border border-dashed border-white/15 rounded-xl text-zinc-500 hover:text-white hover:border-[#C9A84C]/40 transition-all text-sm flex items-center justify-center gap-2"
            >
              <Plus size={14} /> Oefening toevoegen
            </button>
            <button
              onClick={() => setShowSupersetPicker(true)}
              data-testid="add-superset-btn"
              className="flex-1 py-2.5 border border-dashed rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
              style={{ borderColor: `${GOLD}40`, color: GOLD }}
            >
              <Link2 size={13} /> Superset toevoegen
            </button>
          </div>
        </div>
      )}

      {showPicker && (
        <ExPickerModal token={token} onSelect={addOefening} onClose={() => setShowPicker(false)} />
      )}
      {showSupersetPicker && (
        <SupersetPickerModal token={token} onSelect={addSuperset} onClose={() => setShowSupersetPicker(false)} />
      )}
    </div>
  );
}

// ── Hoofd pagina ──────────────────────────────────────────────────────────────
export default function CompanySchemaEditor() {
  const { schemaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [naam, setNaam] = useState("");
  const [doel, setDoel] = useState("Spieropbouw");
  const [niveau, setNiveau] = useState("Beginner");
  const [trainingsdagen, setTrainingsdagen] = useState(3);
  const [beschrijving, setBeschrijving] = useState("");
  const [folderId, setFolderId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [dagen, setDagen] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const isEdit = !!schemaId;

  const load = useCallback(async () => {
    if (!user?.token) return;
    const h = { Authorization: `Bearer ${user.token}` };

    // Mappen ophalen
    const fr = await fetch(`${API}/api/company/schema-folders`, { headers: h });
    if (fr.ok) { const d = await fr.json(); setFolders(d.folders || []); }

    // Schema ophalen als bewerken
    if (isEdit) {
      const sr = await fetch(`${API}/api/company/custom-schemas/${schemaId}`, { headers: h });
      if (sr.ok) {
        const s = await sr.json();
        setNaam(s.naam || "");
        setDoel(s.doel || "Spieropbouw");
        setNiveau(s.niveau || "Beginner");
        setTrainingsdagen(s.trainingsdagen || 3);
        setBeschrijving(s.beschrijving || "");
        setFolderId(s.folder_id || null);
        // Zorg dat elke oefening een ID heeft
        setDagen((s.dagen || []).map(dag => ({
          ...dag,
          oefeningen: (dag.oefeningen || []).map(ex => {
            if (ex.type === "superset") {
              return {
                ...ex,
                id: ex.id || uid(),
                oefeningen: (ex.oefeningen || []).map(sub => ({ ...sub, id: sub.id || uid() })),
              };
            }
            return { ...ex, id: ex.id || uid() };
          }),
        })));
      }
    }
  }, [user, schemaId, isEdit]);

  useEffect(() => { load(); }, [load]);

  const addDag = () => {
    setDagen(d => [...d, { naam: `Dag ${d.length + 1}`, oefeningen: [] }]);
  };

  const removeDag = (idx) => {
    setDagen(d => d.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!naam.trim()) { setToast("Vul een schemanaam in"); return; }
    setSaving(true);
    const body = { naam, doel, niveau, trainingsdagen, beschrijving, folder_id: folderId, dagen };
    const h = { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` };
    try {
      const url = isEdit
        ? `${API}/api/company/custom-schemas/${schemaId}`
        : `${API}/api/company/custom-schemas`;
      const r = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: h, body: JSON.stringify(body) });
      if (!r.ok) throw new Error("Opslaan mislukt");
      navigate("/bedrijf-admin?tab=schemas");
    } catch (err) { setToast(err.message); setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-[#080808]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-14">
          <button onClick={() => navigate("/bedrijf-admin?tab=schemas")}
            className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-sm">
            <ArrowLeft size={15} /> Terug
          </button>
          <div className="flex-1" />
          <span className="text-zinc-500 text-sm hidden sm:block">{isEdit ? "Schema bewerken" : "Nieuw schema"}</span>
          <button
            onClick={handleSave} disabled={saving} data-testid="save-schema-btn"
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-black text-sm font-bold disabled:opacity-40 transition-all"
            style={{ background: GOLD }}
          >
            <Save size={14} /> {saving ? "Opslaan..." : "Schema opslaan"}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-2">
            {isEdit ? "Schema bewerken" : "Nieuw schema aanmaken"}
          </p>
          <input
            value={naam} onChange={e => setNaam(e.target.value)}
            placeholder="Schema naam..."
            data-testid="schema-naam-input"
            className="w-full bg-transparent text-white font-black text-3xl outline-none placeholder:text-zinc-700 border-b border-white/10 pb-2 focus:border-[#C9A84C]/40 transition-colors"
          />
        </div>

        {/* Meta velden */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Doel</span>
            <select value={doel} onChange={e => setDoel(e.target.value)}
              className="bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#C9A84C]/40 appearance-none">
              {DOELEN.map(d => <option key={d}>{d}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Niveau</span>
            <select value={niveau} onChange={e => setNiveau(e.target.value)}
              className="bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#C9A84C]/40 appearance-none">
              {NIVEAUS.map(n => <option key={n}>{n}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Trainingen/week</span>
            <input type="number" min={1} max={7} value={trainingsdagen} onChange={e => setTrainingsdagen(parseInt(e.target.value) || 1)}
              className="bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#C9A84C]/40" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Map</span>
            <select value={folderId || ""} onChange={e => setFolderId(e.target.value || null)}
              className="bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#C9A84C]/40 appearance-none">
              <option value="">Geen map</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.naam}</option>)}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1.5 mb-8">
          <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Beschrijving (optioneel)</span>
          <textarea value={beschrijving} onChange={e => setBeschrijving(e.target.value)} rows={2}
            placeholder="Korte omschrijving van het schema..."
            className="bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm outline-none resize-none focus:border-[#C9A84C]/40 placeholder:text-zinc-600" />
        </label>

        {/* Trainingsdagen */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">Trainingsdagen <span className="text-zinc-600 font-normal text-sm ml-1">({dagen.length})</span></h2>
          <button onClick={addDag} data-testid="add-dag-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-black text-sm font-bold transition-all hover:scale-[1.02] active:scale-95"
            style={{ background: GOLD }}>
            <Plus size={14} /> Dag toevoegen
          </button>
        </div>

        {dagen.length === 0 && (
          <div className="text-center py-12 border border-dashed border-white/8 rounded-2xl mb-6">
            <Dumbbell size={32} className="text-zinc-800 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Nog geen trainingsdagen — klik "Dag toevoegen"</p>
          </div>
        )}

        {dagen.map((dag, idx) => (
          <div key={idx} className="relative">
            <DagEditor dag={dag} onChange={updated => setDagen(d => d.map((x, i) => i === idx ? updated : x))} token={user?.token} />
            <button onClick={() => removeDag(idx)}
              className="absolute top-3 right-3 text-zinc-700 hover:text-red-400 transition-colors p-1"
              title="Dag verwijderen" data-testid={`remove-dag-${idx}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}

        <div className="mt-6 flex justify-end">
          <button onClick={handleSave} disabled={saving} data-testid="save-schema-bottom-btn"
            className="flex items-center gap-2 px-8 py-3 rounded-xl text-black font-bold disabled:opacity-40 transition-all hover:scale-[1.02]"
            style={{ background: GOLD }}>
            <Save size={16} /> {saving ? "Opslaan..." : "Schema opslaan"}
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-red-900/80 border border-red-500/30 text-red-200 text-sm px-5 py-3 rounded-xl backdrop-blur-sm">
          {toast}
          <button onClick={() => setToast(null)} className="ml-3 text-red-400 hover:text-white"><X size={12} /></button>
        </div>
      )}
    </div>
  );
}
